import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CrudForm, CrudStyles } from '../shared/Crud';
import AddressForm from '../shared/AddressForm';
import PhoneNumbersInput from '../shared/PhoneNumbersInput';
import { useForm } from '../../hooks/useForm';
import legalPersonService from '../../services/legalPersonService';
import { buildAddressDtoFromForm, mapBasicLegalPersonAddressToForm } from '../../utils/legalPersonMappers';
import { maskCNPJ, maskCPF, normalizeCNPJ } from '../../utils/masks';
import { REGISTRATION_STATUS_OPTIONS } from '../../utils/registrationStatus';
import { isValidCNPJ, isValidCPF, isValidEmail, isValidZipCode } from '../../utils/validation';
import {
    formatLegalPersonCreateApiErrors,
    mapLegalPersonCreateApiLocation,
} from '../../utils/legalPersonApiErrors';

const { FormGroup, FormRow, Input, SectionTitle, Select } = CrudStyles;

const CNPJ_LOOKUP_DEBOUNCE_MS = 450;

const REGISTRATION_STATUS_SET = new Set(REGISTRATION_STATUS_OPTIONS.map((o) => o.value));

function normalizeApiStatus(status) {
    if (status == null || status === '') return null;
    const key = String(status).trim().toUpperCase();
    return REGISTRATION_STATUS_SET.has(key) ? key : null;
}

const initialFormData = {
    name: '',
    fantasyName: '',
    document: '',
    legalStatus: 'ACTIVE',
    responsibleName: '',
    responsibleEmail: '',
    responsibleDocument: '',
    responsibleBirthdate: '',
};

/**
 * Cadastro de pessoa jurídica (cliente): consulta automática por CNPJ completo + dados do responsável.
 */
function collectValidResponsiblePhones(phones) {
    return (phones || [])
        .map((p) => String(p || '').trim())
        .filter((p) => p.length > 0)
        .filter((p) => {
            const d = p.replace(/\D/g, '');
            return d.length >= 10 && d.length <= 11;
        });
}

export default function LegalPersonForm({ onCancel, onSuccess }) {
    const addressRef = useRef({});
    const documentRef = useRef('');
    const cnpjFetchGenRef = useRef(0);
    const lastLookupDigitsRef = useRef('');
    const [phones, setPhones] = useState(['']);
    const phonesRef = useRef(phones);
    const [basicHint, setBasicHint] = useState('');
    const [fetchingBasic, setFetchingBasic] = useState(false);
    const [responsibleAddressInitial, setResponsibleAddressInitial] = useState(() => ({}));
    const [addressFormKey, setAddressFormKey] = useState(0);

    useEffect(() => {
        phonesRef.current = phones;
    }, [phones]);

    const validateForm = useCallback((data) => {
        const err = {};
        const addr = addressRef.current;

        if (!data.name?.trim()) err.name = 'Razão social é obrigatória';
        if (!data.fantasyName?.trim()) err.fantasyName = 'Nome fantasia é obrigatório';
        if (!data.document?.trim()) err.document = 'CNPJ é obrigatório';
        else if (!isValidCNPJ(data.document)) err.document = 'CNPJ inválido';
        if (!data.legalStatus) err.legalStatus = 'Situação cadastral é obrigatória';

        if (!data.responsibleName?.trim()) err.responsibleName = 'Nome do responsável é obrigatório';
        if (!data.responsibleEmail?.trim()) err.responsibleEmail = 'E-mail do responsável é obrigatório';
        else if (!isValidEmail(data.responsibleEmail)) err.responsibleEmail = 'E-mail inválido';

        if (collectValidResponsiblePhones(phonesRef.current).length === 0) {
            err.phones = 'Informe pelo menos um telefone válido';
        }

        const cpfDigits = (data.responsibleDocument || '').replace(/\D/g, '');
        if (cpfDigits.length > 0 && !isValidCPF(data.responsibleDocument)) {
            err.responsibleDocument = 'CPF inválido';
        }

        if (!addr.zipCode) err.zipCode = 'CEP é obrigatório';
        else if (!isValidZipCode(addr.zipCode)) err.zipCode = 'CEP inválido';
        if (!addr.street?.trim()) err.street = 'Rua é obrigatória';
        if (!addr.number?.trim()) err.number = 'Número é obrigatório';
        if (!addr.district?.trim()) err.district = 'Bairro é obrigatório';
        if (!addr.city?.trim()) err.city = 'Cidade é obrigatória';
        if (!addr.stateAbbr?.trim()) err.stateAbbr = 'UF é obrigatório';

        return err;
    }, []);

    const handleApiSubmit = useCallback(
        async (data) => {
            const cpfDigits = (data.responsibleDocument || '').replace(/\D/g, '');
            const payload = {
                responsibleName: data.responsibleName.trim(),
                responsibleEmail: data.responsibleEmail.trim(),
                responsiblePhoneNumbers: collectValidResponsiblePhones(phonesRef.current).map((p) => ({ number: p })),
                responsibleDocument: cpfDigits.length === 11 ? maskCPF(cpfDigits) : null,
                responsibleBirthdate: data.responsibleBirthdate?.trim() || null,
                responsibleAddress: buildAddressDtoFromForm(addressRef.current),
                name: data.name.trim(),
                fantasyName: data.fantasyName.trim(),
                legalStatus: data.legalStatus,
                document: maskCNPJ(data.document || ''),
                sendRegistrationNotification: false,
            };
            await legalPersonService.createLegalPerson(payload);
            onSuccess?.();
        },
        [onSuccess]
    );

    const legalPersonApiErrorOptions = useMemo(
        () => ({
            mapApiFieldLocation: mapLegalPersonCreateApiLocation,
            formatApiErrors: formatLegalPersonCreateApiErrors,
        }),
        []
    );

    const { formData, errors, handleChange, handleSubmit, setField, setErrors, isSubmitting } = useForm(
        initialFormData,
        handleApiSubmit,
        validateForm,
        legalPersonApiErrorOptions
    );

    useEffect(() => {
        documentRef.current = formData.document ?? '';
    }, [formData.document]);

    useEffect(() => {
        const cleaned = normalizeCNPJ(formData.document || '');
        if (cleaned.length !== 14 || !isValidCNPJ(cleaned)) {
            cnpjFetchGenRef.current += 1;
            lastLookupDigitsRef.current = '';
            setFetchingBasic(false);
            return;
        }

        cnpjFetchGenRef.current += 1;
        const gen = cnpjFetchGenRef.current;

        const timer = setTimeout(async () => {
            const cleanedNow = normalizeCNPJ(documentRef.current || '');
            if (cleanedNow.length !== 14 || !isValidCNPJ(cleanedNow) || gen !== cnpjFetchGenRef.current) {
                return;
            }
            if (cleanedNow === lastLookupDigitsRef.current) {
                return;
            }

            setFetchingBasic(true);
            setBasicHint('');

            try {
                const basic = await legalPersonService.getBasicDataByCnpj(cleanedNow);
                if (gen !== cnpjFetchGenRef.current) {
                    return;
                }
                const stillSame = normalizeCNPJ(documentRef.current || '') === cleanedNow;
                if (!stillSame) {
                    return;
                }

                lastLookupDigitsRef.current = cleanedNow;

                setField('name', basic.name || '');
                setField('fantasyName', basic.fantasyName || '');
                setField('document', maskCNPJ(basic.document || cleanedNow));
                const st = normalizeApiStatus(basic.status);
                if (st) setField('legalStatus', st);

                const mapped = mapBasicLegalPersonAddressToForm(basic.address);
                setResponsibleAddressInitial(mapped);
                setAddressFormKey((k) => k + 1);
                addressRef.current = { ...mapped };

                setBasicHint('');
            } catch {
                if (gen !== cnpjFetchGenRef.current) {
                    return;
                }
                lastLookupDigitsRef.current = '';
                setBasicHint(
                    'Não foi possível consultar os dados automaticamente. Preencha razão social, nome fantasia, situação cadastral e endereço do responsável manualmente.'
                );
            } finally {
                if (gen === cnpjFetchGenRef.current) {
                    setFetchingBasic(false);
                }
            }
        }, CNPJ_LOOKUP_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [formData.document, setField]);

    const handleCompanyDocumentChange = (e) => {
        handleChange({
            target: { name: 'document', value: maskCNPJ(e.target.value) },
        });
        if (basicHint) setBasicHint('');
    };

    const handleResponsibleCpfChange = (e) => {
        handleChange({
            target: { name: 'responsibleDocument', value: maskCPF(e.target.value) },
        });
    };

    const phoneErrors = useMemo(() => {
        if (!errors.phones) return {};
        return { 0: errors.phones };
    }, [errors.phones]);

    return (
        <CrudForm title="Nova pessoa jurídica" onSubmit={handleSubmit} onCancel={onCancel} isSubmitting={isSubmitting}>
            <SectionTitle>Dados da empresa</SectionTitle>
            <FormRow>
                <FormGroup>
                    <label htmlFor="pj-document">CNPJ *</label>
                    <Input
                        id="pj-document"
                        name="document"
                        type="text"
                        value={formData.document}
                        onChange={handleCompanyDocumentChange}
                        $hasError={!!errors.document}
                        placeholder="00.000.000/0001-00"
                        maxLength={18}
                        autoCapitalize="characters"
                        style={{ textTransform: 'uppercase' }}
                    />
                    {errors.document && <span className="error">{errors.document}</span>}
                    {fetchingBasic && (
                        <p style={{ margin: '8px 0 0', color: '#666', fontSize: 14 }} aria-live="polite">
                            Consultando dados da empresa…
                        </p>
                    )}
                    {basicHint && (
                        <p style={{ margin: '8px 0 0', color: '#666', fontSize: 14, lineHeight: 1.45 }} aria-live="polite">
                            {basicHint}
                        </p>
                    )}
                </FormGroup>
            </FormRow>

            <FormRow>
                <FormGroup>
                    <label htmlFor="pj-name">Razão social *</label>
                    <Input
                        id="pj-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        $hasError={!!errors.name}
                    />
                    {errors.name && <span className="error">{errors.name}</span>}
                </FormGroup>
                <FormGroup>
                    <label htmlFor="pj-fantasy">Nome fantasia *</label>
                    <Input
                        id="pj-fantasy"
                        name="fantasyName"
                        value={formData.fantasyName}
                        onChange={handleChange}
                        $hasError={!!errors.fantasyName}
                    />
                    {errors.fantasyName && <span className="error">{errors.fantasyName}</span>}
                </FormGroup>
            </FormRow>

            <FormRow>
                <FormGroup>
                    <label htmlFor="pj-legalStatus">Situação cadastral *</label>
                    <Select
                        id="pj-legalStatus"
                        name="legalStatus"
                        value={formData.legalStatus}
                        onChange={handleChange}
                        $hasError={!!errors.legalStatus}
                    >
                        {REGISTRATION_STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Select>
                    {errors.legalStatus && <span className="error">{errors.legalStatus}</span>}
                </FormGroup>
            </FormRow>

            <SectionTitle>Responsável pela empresa</SectionTitle>
            <FormRow>
                <FormGroup>
                    <label htmlFor="pj-resp-name">Nome completo *</label>
                    <Input
                        id="pj-resp-name"
                        name="responsibleName"
                        value={formData.responsibleName}
                        onChange={handleChange}
                        $hasError={!!errors.responsibleName}
                    />
                    {errors.responsibleName && <span className="error">{errors.responsibleName}</span>}
                </FormGroup>
                <FormGroup>
                    <label htmlFor="pj-resp-email">E-mail *</label>
                    <Input
                        id="pj-resp-email"
                        name="responsibleEmail"
                        type="email"
                        value={formData.responsibleEmail}
                        onChange={handleChange}
                        $hasError={!!errors.responsibleEmail}
                    />
                    {errors.responsibleEmail && <span className="error">{errors.responsibleEmail}</span>}
                </FormGroup>
            </FormRow>

            <PhoneNumbersInput
                label="Telefones do responsável *"
                initialPhones={phones}
                onChange={(next) => {
                    setPhones(next);
                    if (errors.phones) {
                        setErrors((prev) => {
                            const { phones: _p, ...rest } = prev;
                            return rest;
                        });
                    }
                }}
                errors={phoneErrors}
            />

            <FormRow>
                <FormGroup>
                    <label htmlFor="pj-resp-cpf">CPF (opcional)</label>
                    <Input
                        id="pj-resp-cpf"
                        name="responsibleDocument"
                        value={formData.responsibleDocument}
                        onChange={handleResponsibleCpfChange}
                        $hasError={!!errors.responsibleDocument}
                        placeholder="000.000.000-00"
                        maxLength={14}
                    />
                    {errors.responsibleDocument && <span className="error">{errors.responsibleDocument}</span>}
                </FormGroup>
                <FormGroup>
                    <label htmlFor="pj-resp-birth">Data de nascimento (opcional)</label>
                    <Input
                        id="pj-resp-birth"
                        name="responsibleBirthdate"
                        type="date"
                        value={formData.responsibleBirthdate}
                        onChange={handleChange}
                        $hasError={!!errors.responsibleBirthdate}
                    />
                    {errors.responsibleBirthdate && (
                        <span className="error">{errors.responsibleBirthdate}</span>
                    )}
                </FormGroup>
            </FormRow>

            <SectionTitle>Endereço do responsável</SectionTitle>
            <AddressForm
                key={addressFormKey}
                initialAddress={responsibleAddressInitial}
                onChange={(addr) => {
                    addressRef.current = addr;
                }}
                errors={{
                    zipCode: errors.zipCode,
                    street: errors.street,
                    number: errors.number,
                    complement: errors.complement,
                    district: errors.district,
                    city: errors.city,
                    stateAbbr: errors.stateAbbr,
                }}
                customerId={null}
            />

            {errors.submit && (
                <p className="error" style={{ margin: 0, whiteSpace: 'pre-line' }}>
                    {errors.submit}
                </p>
            )}
        </CrudForm>
    );
}
